/**
 * stampPackageExtras — write the package tenants into a GLB's bytes.
 *
 * The export door already produced the avatar (no clips, textures derived,
 * seating and arm-space declared on their bones). The package's own three
 * declarations ride the ROOT node, and they are written HERE, into the JSON
 * chunk, after the body is final — because the record inside the file
 * describes that body (its sha256, its census), and a record cannot describe
 * bytes that still change. So: export → derive → sha256 + census of the BODY
 * → compose the record → stamp. The BIN chunk is copied untouched; only the
 * JSON chunk grows and the header length follows.
 *
 * Same tenants and validation as the userData path (`package-extras.ts`), so
 * a file stamped here reads back through `parse*Marker` on the loader's
 * userData exactly as if the scene had carried them.
 *
 * Depends on: package-extras, package-census (the reader). Depended on by:
 * the package door, the reopen census.
 */
import { withHeightMarker, withPackageMarker, withThumbnailMarker, type HeightMarker } from './package-extras.js';
import { PackageCensusError } from './package-census.js';
import type { PackageRecord } from './package.js';

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;

export interface PackageTenants {
  readonly height: HeightMarker | null;
  readonly record: PackageRecord | null;
  /** A PNG data URI, or null to leave the file without a card. */
  readonly thumbnail: string | null;
}

interface Chunk {
  readonly type: number;
  readonly data: Uint8Array;
}

function chunksOf(glb: ArrayBuffer): Chunk[] {
  const view = new DataView(glb);
  if (glb.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC) throw new PackageCensusError('not a GLB (bad magic)');
  const out: Chunk[] = [];
  let cursor = 12;
  while (cursor + 8 <= glb.byteLength) {
    const length = view.getUint32(cursor, true);
    const type = view.getUint32(cursor + 4, true);
    out.push({ type, data: new Uint8Array(glb, cursor + 8, length) });
    cursor += 8 + length + ((4 - (length % 4)) % 4);
  }
  if (out[0]?.type !== CHUNK_JSON) throw new PackageCensusError('first chunk is not JSON');
  return out;
}

/**
 * The root node: the first node of the default scene. A file with no scene
 * node has nowhere to carry a declaration and refuses.
 */
function rootNodeOf(json: Record<string, unknown>): Record<string, unknown> {
  const scenes = json['scenes'] as { nodes?: number[] }[] | undefined;
  const sceneIndex = (json['scene'] as number | undefined) ?? 0;
  const rootIndex = scenes?.[sceneIndex]?.nodes?.[0];
  const nodes = json['nodes'] as Record<string, unknown>[] | undefined;
  const root = rootIndex === undefined ? undefined : nodes?.[rootIndex];
  if (root === undefined) throw new PackageCensusError('the GLB has no root scene node to carry the package declarations');
  return root;
}

export function stampPackageExtras(glb: ArrayBuffer, tenants: PackageTenants): ArrayBuffer {
  const chunks = chunksOf(glb);
  const json = JSON.parse(new TextDecoder().decode(chunks[0]!.data)) as Record<string, unknown>;
  const root = rootNodeOf(json);
  let extras: unknown = root['extras'];
  extras = withHeightMarker(extras, tenants.height);
  extras = withPackageMarker(extras, tenants.record);
  extras = withThumbnailMarker(extras, tenants.thumbnail);
  if (Object.keys(extras as Record<string, unknown>).length === 0) delete root['extras'];
  else root['extras'] = extras;

  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPad = (4 - (jsonBytes.length % 4)) % 4;
  const rest = chunks.slice(1);
  const restTotal = rest.reduce((n, c) => n + 8 + c.data.length + ((4 - (c.data.length % 4)) % 4), 0);
  const total = 12 + 8 + jsonBytes.length + jsonPad + restTotal;
  const out = new ArrayBuffer(total);
  const dv = new DataView(out);
  const u8 = new Uint8Array(out);
  dv.setUint32(0, GLB_MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, total, true);
  dv.setUint32(12, jsonBytes.length + jsonPad, true);
  dv.setUint32(16, CHUNK_JSON, true);
  u8.set(jsonBytes, 20);
  u8.fill(0x20, 20 + jsonBytes.length, 20 + jsonBytes.length + jsonPad);
  let at = 20 + jsonBytes.length + jsonPad;
  for (const c of rest) {
    dv.setUint32(at, c.data.length, true);
    dv.setUint32(at + 4, c.type, true);
    u8.set(c.data, at + 8);
    at += 8 + c.data.length + ((4 - (c.data.length % 4)) % 4);
  }
  return out;
}
